<template>
    <div :class="isMobile ? 'my-5 mx-4' : 'my-0 mx-0'">
        <v-card elevation="1">
            <v-toolbar flat color="white">
                <v-toolbar-title class="h6 ml-2">Configuración</v-toolbar-title>
            </v-toolbar>
            <div class="px-4 pb-6">
                <span class="ml-2 text-caption">En esta sección puedes configurar tu sistema de riego </span>
                <!-- Cabezal -->
                <v-col cols="12" sm="6" md="4" class="mt-2">
                    <v-card class="card-section-wrapper" elevation="1">
                        <!-- Título arriba -->
                        <p class="text-h6 mb-2" style="font-weight: bold;">Cabezal</p>

                        <!-- Descripción / Nota -->
                        <p class="mb-4 d-flex align-center" style="color: #6d6d6d;">
                            <v-icon size="18" class="mr-1" color="blue darken-2">mdi-information-outline</v-icon>
                            <span class="text-pequeno"><strong>Nota:</strong> El cabezal es el variador usado para
                                controlar el riego
                                sectorial.</span>
                        </p>
                        <span>
                            <div v-if="loadingCabezal" class="custom-spinner-wrapper">
                                <div v-if="!showMainLoader" class="dot-spinner">
                                    <span v-for="i in 8" :key="i" :style="getDotStyle(i)"></span>
                                </div>
                            </div>

                            <v-card v-else-if="!cabezalVariador && !loadingCabezal"
                                class="d-flex align-center justify-center add-card-inner" elevation="1"
                                @click="onAddHeadClick">
                                <v-icon color="teal darken-3" size="40">mdi-plus-circle</v-icon>
                            </v-card>

                            <v-card v-else-if="cabezalVariador && !loadingCabezal"
                                class="d-flex flex-column align-center justify-center card-cabezal-inner position-relative"
                                elevation="1">
                                <!-- Botón de edición -->
                                <v-btn fab dark x-small color="orange darken-1" class="edit-btn" @click="editCabezal">
                                    <v-icon>mdi-pencil</v-icon>
                                </v-btn>

                                <v-icon color="white" size="40">mdi-gauge</v-icon>
                                <span class="text-subtitle-2 font-weight-bold mt-2 text-white text-center">
                                    {{ cabezalVariador.nombre }}
                                </span>
                            </v-card>
                        </span>
                    </v-card>
                </v-col>
            </div>
        </v-card>
        <v-dialog v-model="addCabezalDialog" :max-width="isMobile ? '' : '560px'" :fullscreen="isMobile">
            <v-card class="elevation-2">
                <!-- Header -->
                <v-card-title class="d-flex justify-space-between align-center">
                    <span class="h6 font-weight-bold">
                        {{ isEditingCabezal ? 'Editar Cabezal' : 'Añadir Cabezal' }}
                    </span>
                    <v-btn icon @click="closeAddDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>

                </v-card-title>
                <!-- Body -->
                <v-card-text class="pa-6">
                    <v-form ref="formRef" v-model="formIsValid">
                        <!-- Título agrupador -->
                        <h4 class="text-subtitle-2 font-weight-medium mb-2" style="color: black">Selección de variador
                        </h4>
                        <v-select v-model="variador" :items="variadores" class="my-2" color="teal darken-2"
                            label="Selecciona un variador" item-text="nombre" item-value="id" outlined rounded
                            :rules="[v => !!v || 'Debes seleccionar un variador']" item-color="teal darken-2">
                            <template v-slot:selection="data">
                                <v-icon left large class="ml-2 mr-3">mdi-gauge</v-icon>
                                {{ data.item.nombre }}
                            </template>
                            <template v-slot:item="data">
                                <template>
                                    <v-list-item-avatar color="teal">
                                        <v-icon color="white">mdi-gauge</v-icon>
                                    </v-list-item-avatar>
                                    <v-list-item-content>
                                        <v-list-item-title> {{ data.item.nombre }} </v-list-item-title>
                                    </v-list-item-content>
                                </template>
                            </template>
                        </v-select>
                        <h4 class="text-subtitle-2 font-weight-medium mb-5" style="color: black">Configuración técnica
                        </h4>
                        <!-- Parámetros -->
                        <v-row dense>
                            <v-col cols="12">
                                <v-text-field v-model="presionMaxima" label="Presión máxima (3 - 20)" outlined
                                    prepend-inner-icon="mdi-car-brake-low-pressure" type="number" rounded
                                    density="comfortable" color="teal darken-2"
                                    :rules="[(v) => v >= 3 && v <= 20 || 'Debe estar entre 3 y 20']" class="mb-3">
                                    <template #append-inner>
                                        <span class="text-caption text-grey-darken-1">bar</span>
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-select v-model="nEntrada" label="Nº de entrada"
                                    prepend-inner-icon="mdi-arrow-down-bold" :items="[1, 2, 3, 4]" outlined rounded
                                    density="comfortable" color="teal darken-2" class="mb-3" />
                            </v-col>
                            <v-col cols="12">
                                <v-select v-model="nSalida" label="Nº de salida" prepend-inner-icon="mdi-arrow-up-bold"
                                    outlined :items="[1, 2, 3, 4]" rounded density="comfortable" class="mb-3"
                                    color="teal darken-2" />
                            </v-col>
                            <v-col cols="12">
                                <v-select v-model="criterio" :items="criterios" item-text="title" item-value="value"
                                    label="Criterio" prepend-inner-icon="mdi-compare" outlined rounded
                                    color="teal darken-2" />
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <!-- Footer -->
                <v-divider />
                <v-card-actions class="pa-4 justify-end">
                    <v-btn text color="red darken-2"
                        @click="isEditingCabezal ? confirmDeleteDialog = true : closeAddDialog()">
                        {{ isEditingCabezal ? 'Eliminar' : 'Cancelar' }}
                    </v-btn>

                    <v-btn text color="teal darken-2" :disabled="!formIsValid" @click="submitCabezal">
                        {{ isEditingCabezal ? 'Actualizar' : 'Guardar' }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Diálogo de confirmación para eliminar -->
        <v-dialog v-model="confirmDeleteDialog" max-width="500px">
            <v-card>
                <v-card-title class="headline text-subtitle-1 mt-3">Eliminar cabezal</v-card-title>
                <v-card-text class="text-subtitle-2" style="color:#6d6d6d">
                    <template v-if="cabezalVariador">
                        ¿Estás seguro de que deseas eliminar el cabezal <strong>{{ cabezalVariador.nombre }}</strong>?
                    </template>
                    <br><br>
                    <v-icon color="red darken-2" class="mr-2">mdi-alert-circle</v-icon>
                    <span class="font-weight-bold">¡Atención!</span>
                    <br>
                    <span> Si eliminas el cabezal no podrá usarlo para el riego sectorial.</span>
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn text color="teal darken-1" @click="confirmDeleteDialog = false">Cancelar</v-btn>
                    <v-btn text color="red darken-1" @click="handleDeleteCabezal">Confirmar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Snackbar para mostrar mensajes -->
        <v-snackbar v-model="showSnackbar" :timeout="2500" :color="snackbarColor">
            {{ snackbarMessage }}
        </v-snackbar>
    </div>
</template>


<script>
import { nextTick } from 'vue';

export default {
    props: ["variadores", "loading"],
    data() {
        return {
            addCabezalDialog: false,
            isEditingCabezal: false,
            variador: null,
            presionMaxima: 3,
            nEntrada: 1,
            nSalida: 1,
            criterio: 'min',
            criterios: [
                { title: 'Presión mínima', value: 'min' },
                { title: 'Presión máxima', value: 'max' }
            ],
            formIsValid: false,
            confirmDeleteDialog: false,
            showSnackbar: false,
            snackbarMessage: '',
            snackbarColor: 'success',
            showMainLoader: false,

        };
    },

    computed: {
        isMobile() {
            return this.$vuetify.breakpoint.smAndDown;
        },

        loadingCabezal() {
            return this.loading
        },

        variadoresList() {
            return this.variadores || [];
        },

        cabezalVariador() {
            return this.variadoresList.find(v => v.is_cabezal === 1) || null;
        }
    },

    methods: {
        onAddHeadClick() {
            this.addCabezalDialog = true;
        },
        async closeAddDialog() {
            this.addCabezalDialog = false;
            this.presionMaxima = 3;
            this.nEntrada = 1;
            this.nSalida = 1;
            this.criterio = 'min';
            this.variador = null;
            this.isEditingCabezal = false;

            await this.$nextTick(); // Esperamos a que se cierre visualmente el diálogo
            if (this.$refs.formRef) {
                this.$refs.formRef.resetValidation(); // Limpiar validación
            }

        },
        handleDeleteCabezal() {
            this.$emit('onDeleteCabezal', this.cabezalVariador.id);
            this.confirmDeleteDialog = false;
            this.closeAddDialog();
        },
        async submitCabezal() {
            const isValid = await this.$refs.formRef.validate();
            if (!isValid) return;
            if (this.isEditingCabezal && this.cabezalVariador) {
                this.$emit('onUpdateCabezal', {
                    previousId: this.cabezalVariador.id,
                    newData: {
                        id: this.variador,
                        presion_maxima: this.presionMaxima,
                        n_entrada: this.nEntrada,
                        n_salida: this.nSalida,
                        criterio: this.criterio
                    }
                })

                this.snackbarMessage = 'Cabezal actualizado correctamente';
                this.snackbarColor = 'orange darken-1'
            } else {
                this.$emit('onInsertCabezal', {
                    id: this.variador,
                    presion_maxima: this.presionMaxima,
                    n_entrada: this.nEntrada,
                    n_salida: this.nSalida,
                    criterio: this.criterio
                });
                this.snackbarMessage = 'Cabezal añadido correctamente';
                this.snackbarColor = 'green';
            }

            this.showSnackbar = true;
            this.closeAddDialog();
        },

        getDotStyle(index) {
            const angle = (index - 1) * 45
            return {
                transform: `rotate(${angle}deg) translate(0, -20px)`,
                animationDelay: `${index * 0.35}s`,

            }
        },

        async editCabezal() {
            if (!this.cabezalVariador) return;

            this.isEditingCabezal = true;

            const formList = this.variadoresList.find(v => v.id === this.cabezalVariador.id);
            this.variador = formList.id;

            await nextTick()

            this.presionMaxima = this.cabezalVariador.presion_maxima;
            this.nEntrada = this.cabezalVariador.n_entrada;
            this.nSalida = this.cabezalVariador.n_salida;
            this.criterio = this.cabezalVariador.criterio;
            this.variador = this.cabezalVariador.id;

            this.addCabezalDialog = true;
        }

    },
    onMounted() {
        if (this.loading) {
            setTimeout(() => {
                this.showMainLoader = true
            }, 1500)
        }
    }
}

</script>
<style scoped>
.card-section-wrapper {
    background-color: #f0f4f8;
    padding: 24px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
}

.add-card-inner {
    height: 120px;
    border: 1.5px dashed #ccc;
    border-radius: 12px;
    background-color: white;
    cursor: pointer;
    transition: box-shadow 0.2s ease;
}

.add-card-inner:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.card-cabezal-inner {
    height: 120px;
    background-color: #099ba8;
    color: white;
    border-radius: 12px;
    position: relative;
}

.edit-btn {
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 2;
}

.text-pequeno {
    font-size: 0.7rem;
}

.custom-spinner-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 120px;
    background-color: white;
    border: 1px dashed #ccc;
    border-radius: 12px;
}

.dot-spinner {
    position: relative;
    width: 50px;
    height: 50px;
    animation: rotate 1.2s linear infinite;
}

.dot-spinner span {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 8px;
    height: 8px;
    margin: -4px;
    background: linear-gradient(to right, #1c9271, #0cadc2);
    border-radius: 50%;
    animation: pulse 1.2s ease-in-out infinite;
}

@keyframes pulse {

    0%,
    100% {
        transform: scale(0.5);
        opacity: 0.5;
    }

    50% {
        transform: scale(1.2);
        opacity: 1;
    }
}

@keyframes rotate {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.progress {
    stroke: url(#grad);
    stroke-width: 6;
    stroke-linecap: round;
    stroke-dasharray: 125;
    stroke-dashoffset: 125;
    animation: fillProgress 2s ease-in-out infinite;
}

@keyframes fillProgress {
    0% {
        stroke-dashoffset: 125;
    }

    50% {
        stroke-dashoffset: 0;
    }

    100% {
        stroke-dashoffset: 125;
    }
}
</style>